import { useRef, useState } from 'react';
import type { PhotoKind } from '@salonomy/types';
import { Button, Field, Modal } from '@salonomy/ui';
import { useConfirmPhoto, useInitPhotoUpload } from '../../data/photos.js';
import { uploadToPresignedUrl } from '../../lib/photos-api.js';
import { ApiClientError } from '../../lib/api.js';
import { useToast } from '../../lib/toast.js';
import { isStorageDisabledError } from './photo-shared.js';

/** A sane client-side guard before we even ask the server to presign (the server caps for real). */
const MAX_BYTES = 15 * 1024 * 1024; // 15 MB

/**
 * Read a File's natural pixel dimensions (best-effort) so confirm can record them. Resolves to nulls if the
 * browser can't decode it — confirm tolerates missing dims (the server only stores what we send).
 */
async function readDimensions(file: File): Promise<{ width?: number; height?: number }> {
  try {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('decode failed'));
        el.src = url;
      });
      return { width: img.naturalWidth || undefined, height: img.naturalHeight || undefined };
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch {
    return {};
  }
}

/**
 * WS-13 Feature 5 — the CAPTURE / UPLOAD flow. The three-step contract, faithfully:
 *   1. POST init (clientId, contentType, byteSize, kind, optional pairId) → a presigned PUT ticket;
 *   2. PUT the raw bytes straight to the bucket (uploadToPresignedUrl — NOT through our API);
 *   3. POST confirm (photoId, real dims/byteSize) → the row flips 'pending' → 'ready' and the gallery
 *      re-reads with a mintable signed downloadUrl.
 *
 * STORAGE-DISABLED HONESTY: if step 1 returns 503 (no bucket creds on staging) we DON'T proceed to a broken
 * presigned PUT — we surface "Photo storage isn’t configured yet" and tell the parent (onStorageDisabled)
 * so the whole gallery can switch to the disabled state. The file/camera input uses `capture` so a phone
 * opens the camera directly; `accept="image/*"` keeps it to images (the server re-validates).
 *
 * `kind` is fixed by the caller: a NEW SET starts with a 'before' (no pairId); attaching to an existing set
 * supplies that set's pairId + the kind it's missing (usually 'after').
 */
export function PhotoUploadModal({
  clientId,
  pairId,
  kind,
  title,
  onClose,
  onStorageDisabled,
}: {
  clientId: string;
  /** Omit to mint a NEW set; supply to attach this photo to an existing before/after set. */
  pairId?: string;
  kind: PhotoKind;
  title: string;
  onClose: () => void;
  onStorageDisabled: () => void;
}) {
  const toast = useToast();
  const init = useInitPhotoUpload();
  const confirm = useConfirmPhoto();
  const fileInput = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pick(f: File | null) {
    setError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (!f) {
      setFile(null);
      setPreviewUrl(null);
      return;
    }
    if (!f.type.startsWith('image/')) {
      setError('Pick an image file (JPEG, PNG, HEIC).');
      setFile(null);
      setPreviewUrl(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setError('That image is too large (max 15 MB).');
      setFile(null);
      setPreviewUrl(null);
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  }

  async function submit() {
    if (!file) {
      setError('Choose a photo first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Step 1 — presign. A 503 here is the honest disabled-storage signal.
      const ticket = await init.mutateAsync({
        clientId,
        body: {
          contentType: file.type,
          byteSize: file.size,
          kind,
          ...(pairId ? { pairId } : {}),
        },
      });

      // Step 2 — bytes straight to the bucket (no session cookie; the presigned URL is the capability).
      await uploadToPresignedUrl(ticket, file);

      // Step 3 — confirm: record the real dims/size and flip to 'ready'.
      const dims = await readDimensions(file);
      await confirm.mutateAsync({
        clientId,
        photoId: ticket.id,
        body: { byteSize: file.size, ...dims },
      });

      if (ticket.evictedPairIds.length > 0) {
        toast.success(
          `Photo saved. The oldest set was removed to stay within the ${15}-set limit.`,
        );
      } else {
        toast.success('Photo saved.');
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      onClose();
    } catch (err) {
      if (isStorageDisabledError(err)) {
        // Don't pretend the uploader works — hand the disabled state up and close honestly.
        onStorageDisabled();
        onClose();
        return;
      }
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'Could not save this photo. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      width={460}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!file || busy}>
            {busy ? 'Saving…' : 'Save photo'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field
          label={kind === 'before' ? 'Before photo' : 'After photo'}
          hint="Take a new photo or choose one from this device."
        >
          {/* `capture="environment"` opens the rear camera on a phone; on desktop it's a file picker. */}
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
            style={{ fontSize: 13.5 }}
          />
        </Field>

        {previewUrl ? (
          <div
            style={{
              borderRadius: 10,
              overflow: 'hidden',
              border: '1px solid var(--slm-border)',
              maxHeight: 280,
              display: 'flex',
              justifyContent: 'center',
              background: 'var(--slm-surface-2, #f4f4f5)',
            }}
          >
            {/* Local object-URL preview — the bytes have not been uploaded yet. */}
            <img
              src={previewUrl}
              alt="Selected photo preview"
              style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain' }}
            />
          </div>
        ) : null}

        {error ? (
          <div className="muted" style={{ fontSize: 12.5, color: 'var(--slm-danger, #b42318)' }}>
            {error}
          </div>
        ) : null}

        <p className="muted" style={{ fontSize: 12, margin: 0, lineHeight: 1.5 }}>
          New sets are private to your salon by default. You can make a set visible to the client,
          or (with their consent) add it to your public showcase, from the gallery.
        </p>
      </div>
    </Modal>
  );
}
